"use client";

import React, { useRef } from "react";
import { GlowIndicator, type GlowVariant } from "../../atoms/glow-indicator/glow-indicator";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { TabTray } from "../../atoms/tab-tray/tab-tray";
import { plainText } from "../../atoms/text/text";
import { Pagination } from "../../molecules/pagination/pagination";
import { ACCENT_MS, Bloom } from "./bloom.client";
import { warnInDevelopment, type GrowCarouselCard, type PaintField } from "./carousel-content";
import { GrowCard, type GrowCardChannelLogoLayout } from "./grow-card";
import { paint, text } from "./grow-card-carousel.fields";
import { DEFAULT_GROW_PROFILE } from "./grow-card-carousel.profiles";
import { useRailScroll } from "./rail-scroll.client";
import styles from "./grow-card-carousel.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Applied HERE and not left to Pagination's own "Previous page"/"Next page":
   this rail steps packs, not pages. */
const PREVIOUS_ARROW_LABEL = "Previous pack";
const NEXT_ARROW_LABEL = "Next pack";

/* Named for packs, not pages, like the two arrow labels above — Pagination's
   own default is "Pages". */
const PAGINATION_ARIA_LABEL = "Pack pagination";

/* The other two landmarks the section publishes, and neither has a default of
   its own: TabTray puts `ariaLabel` straight onto the element, and the rail's
   group is a bare <div> declared here. All five are held HERE and NONE is a
   prop, per WAT-12200 decision 2: these are landmark and arrow names reachable
   only through an undesigned blok spread, so an author-settable aria-label
   here would risk a WCAG 2.5.3 Label-in-Name failure. That is narrower than,
   and independent of, whether a CTA's aria-label may be authored — CLAUDE.md
   now says it may. */
const TABS_ARIA_LABEL = "Choose a genre pack";
const RAIL_ARIA_LABEL = "Genre pack cards";

/* ⚠ Pagination greys an arrow out at either end of its list and cannot be
   told not to; this rail WRAPS, so it is handed a middle page that is never
   an end (AC17). Constants, NOT the real index — a plausible wrong number
   would be read as a position. WAT-12188 AC3 owns replacing these with the
   real index and count, and ships NOT DONE: the one-line opt-out belongs to
   the WAT-11665 Pagination owner and has not landed — pagination.tsx:139 is
   still `disablePrevious || isFirstPage || hasNoPages`. The shim stays until
   it does (design-conflicts). */
const WRAPPING_PAGE = 1;
const WRAPPING_PAGE_COUNT = 3;

/* Passed on BOTH branches: GlowIndicator's own band default differs from what
   Pagination hands it, so leaving either to its own default would draw two
   different bars off one CMS switch. Following the molecule rather than the
   frame's own value is deliberate — measurements recorded in design-conflicts. */
const GLOW_BLUR_HEIGHT = 12;
const GLOW_BAR_HEIGHT = 4;

/* WAT-12188 AC2: `variant` no longer carries anything authored — the BAR
   follows the grown card's accent, through `.root .glowBar` re-pointing the
   atom's --bar-color at --gcc-accent. What `variant` still decides is the
   blurred HALO above the bar, whose radial-gradient is hardcoded inside the
   atom per variant and cannot follow an accent from out here.
   `purple` and not the atom's own `slate` default: the two publish the SAME
   gradient and differ only in --glow-opacity, 0.82 against slate's 0, so
   slate is the invisible halo AC7 exists to rule out. Holding `purple` also
   leaves the shipped halo byte-identical to what WAT-12138 released. */
const GLOW_HALO_VARIANT: GlowVariant = "purple";

export interface CarouselShellProps {
  cards: GrowCarouselCard[];
  /** Index for index with `cards`. */
  cardLabels: string[];
  /* heading, bands and glow arrive ALREADY RENDERED, which is what keeps the
     static half of the section on the server. */
  heading?: React.ReactNode;
  bands: React.ReactNode;
  glow: React.ReactNode;
  tabs: boolean;
  pagination: boolean;
  arrows: boolean;
  backdrop: boolean;
  logoLayout: GrowCardChannelLogoLayout;
  linkPosition?: string;
  id?: string;
  /**
   * WAT-11988 AC5 — arrives ALREADY RENDERED, same as `heading`. Centred,
   * below the Tab Tray — rendered after it in the header rather than folded
   * into the `heading` slot, which renders BEFORE the tray and could not put
   * it there without reordering every existing header.
   */
  zipCode?: React.ReactNode;
  /** Development only, and absent in production — see warnInDevelopment. */
  diagnostics?: { authored: number; refusedVariants: string[]; unresolved: string[] };
}

function CarouselShellImpl({
  cards,
  cardLabels,
  heading,
  bands,
  glow,
  tabs,
  pagination,
  arrows,
  backdrop,
  logoLayout,
  linkPosition,
  id,
  zipCode,
  diagnostics
}: CarouselShellProps) {
  const count = cards.length;
  const rail = useRailScroll(cards.map(card => card.id));
  const { activeSlot, middle, repeats } = rail;
  const active = cards[rail.activeIndex];
  /* One card is not a rail — no scroll extent, so the tab stop, role and name
     drop together, and the arrows go with them. */
  const navigable = count > 1;
  const showArrows = arrows && navigable;

  /* Latches each development diagnostic to once per instance. */
  const warnedRef = useRef<Set<string>>(new Set());

  if (process.env.NODE_ENV !== "production" && diagnostics) {
    const paints: [PaintField, unknown][] = [
      ["accent", active?.accent],
      ["accent_deep", active?.accent_deep]
    ];

    warnInDevelopment({
      authored: diagnostics.authored,
      cards,
      refusedPaints: paints
        .filter(([, value]) => text(value) !== "" && paint(value) === undefined)
        .map(([field]) => field),
      refusedVariants: diagnostics.refusedVariants,
      seen: warnedRef.current,
      unresolved: diagnostics.unresolved
    });
  }

  if (count === 0) {
    return null;
  }

  const items = cards.map((card, index) => ({
    label: cardLabels[index],
    value: card.id
  }));

  const hasHeader = Boolean(heading) || tabs || Boolean(zipCode);

  return (
    <section
      className={styles.root}
      /* A constant, so the served markup always carries it; the rail's effect
         overwrites it with the resolved profile. */
      data-gcc-profile={DEFAULT_GROW_PROFILE}
      id={id || undefined}
      ref={rail.rootRef}
      style={
        {
          "--gcc-accent": paint(active?.accent),
          "--gcc-accent-deep": paint(active?.accent_deep),
          "--gcc-accent-duration": `${ACCENT_MS}ms`
        } as React.CSSProperties
      }
    >
      {backdrop && (
        <>
          <Bloom bands={bands} card={active} />
          {glow}
        </>
      )}

      {hasHeader && (
        <header className={styles.header}>
          {heading}

          {tabs && (
            /* Host scroller — lets the tray bleed past the page edges;
               scroll="host" is the atom's own mode. */
            <div className={styles.tabScroll} ref={rail.tabScrollRef}>
              <TabTray
                ariaLabel={TABS_ARIA_LABEL}
                items={items}
                linkPosition={linkPosition}
                onChange={rail.selectCard}
                scroll="host"
                size="small"
                value={active?.id ?? ""}
              />
            </div>
          )}

          {/* WAT-11988 AC5 — centred, below the Tab Tray. Optional: when
              omitted this renders nothing and the header is unchanged. */}
          {zipCode && <div className={styles.zipCode}>{zipCode}</div>}
        </header>
      )}

      {/* Children of the SECTION, not the rail, so the bands run its full
          height the way the frame publishes them — which from 1280 up dims the
          two ends of the header as well as the peeking cards, accepted by
          product on 2026-09-17. Outside the `backdrop` gate: an edge treatment,
          not part of the bloom gradient. */}
      <div aria-hidden="true" className={styles.scrim} />
      <div aria-hidden="true" className={styles.scrimRight} />

      <div className={styles.rail}>
        <div
          aria-label={navigable ? RAIL_ARIA_LABEL : undefined}
          aria-roledescription={navigable ? "carousel" : undefined}
          className={styles.viewport}
          ref={rail.viewportRef}
          role={navigable ? "group" : undefined}
          tabIndex={navigable ? 0 : undefined}
          {...rail.viewportProps}
        >
          <div className={styles.track}>
            {Array.from({ length: count * repeats }, (_, slot) => {
              const card = cards[slot % count];
              /* Only the middle copy is announced — the others are the same
                 cards again (AC30). `inert`, not aria-hidden alone: aria-hidden
                 over a subtree with a focusable CTA leaves it in the tab order. */
              const announced = slot >= middle && slot < middle + count;

              return (
                <div
                  aria-hidden={announced ? undefined : true}
                  aria-label={navigable ? `${(slot % count) + 1} of ${count}` : undefined}
                  aria-roledescription={navigable ? "slide" : undefined}
                  className={styles.slot}
                  data-side={slot === activeSlot ? "active" : slot < activeSlot ? "before" : "after"}
                  key={slot}
                  /* A roledescription needs a role to rename; group is the
                     APG pattern's own choice for a slide. */
                  role={navigable ? "group" : undefined}
                  ref={node => {
                    rail.slotRefs.current[slot] = node;
                    /* Both forms of `inert`: the ref sets the DOM property
                       (client-only), the spread covers SSR. React 18 emits
                       `inert=""` and drops `inert={true}`; React 19 does the
                       reverse — INERT_SLOT picks off React's major. */
                    if (node) {
                      node.inert = !announced;
                    }
                  }}
                  {...(announced ? {} : INERT_SLOT)}
                >
                  <GrowCard
                    /* Spread, not one object prop — an object literal here
                       would fail GrowCard's memo by identity every render. */
                    {...logoLayout}
                    active={slot === activeSlot}
                    card={card}
                    linkPosition={linkPosition}
                    /* Every card prop sits AFTER the spread, and that order is
                       LOAD-BEARING for a caller of THIS component:
                       `logoLayout` is typed to the six sizing fields but
                       nothing stops a wider object at runtime, and declaring
                       each one here is what shadows a stray arriving inside
                       it. */
                    size={slot === activeSlot ? "grown" : "ungrown"}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {navigable && (
        <span aria-live="polite" className="dds-sr-only" role="status">
          {/* The label as a reader HEARS it: the tab beside it draws the same
              authored markup as elements, and interpolating the raw string
              here would spell "<sup>" into the announcement instead. */}
          {`${plainText(cardLabels[rail.announcedIndex] ?? "")}, ${rail.announcedIndex + 1} of ${count}`}
        </span>
      )}

      {pagination && (
        /* Pagination carries no `className`, so the section's own placement
           and width ride this wrapper. Still no per-card indicator (AC37). */
        <div className={styles.pagination}>
          {showArrows ? (
            <Pagination
              ariaLabel={PAGINATION_ARIA_LABEL}
              /* Never an end of the list — see WRAPPING_PAGE above. */
              currentPage={WRAPPING_PAGE}
              glowBarHeight={GLOW_BAR_HEIGHT}
              glowBlurHeight={GLOW_BLUR_HEIGHT}
              /* Carries BOTH the forced-colors edge and the grown card's
                 accent, structurally rather than by the atom's private class
                 names — see .root .glowBar in the stylesheet. */
              glowIndicatorClassName={styles.glowBar}
              nextLabel={NEXT_ARROW_LABEL}
              onNext={() => rail.step(1)}
              onPrevious={() => rail.step(-1)}
              previousLabel={PREVIOUS_ARROW_LABEL}
              totalPages={WRAPPING_PAGE_COUNT}
              variant={GLOW_HALO_VARIANT}
            />
          ) : (
            /* Arrows off leaves the bar alone, which is the atom Pagination
               itself draws — so it is consumed directly rather than rendering
               a landmark with nothing in it to name. */
            <GlowIndicator
              barHeight={GLOW_BAR_HEIGHT}
              blurHeight={GLOW_BLUR_HEIGHT}
              /* Same class as the arrows-on branch, so the bar keeps its
                 accent and its forced-colors edge here too. */
              className={styles.glowBar}
              variant={GLOW_HALO_VARIANT}
              width="100%"
            />
          )}
        </div>
      )}
    </section>
  );
}
export const CarouselShell = withSafeRender(CarouselShellImpl, "CarouselShell");
